import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { Entity } from "@common/types/entity";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { AllFieldsToggle } from "./AllFieldsToggle";
import { FromEntitySelect } from "./FromEntitySelect";
import {
    KICK_SENDER_VALUE,
    useNativeInvoiceSenderOptions,
} from "./useNativeInvoiceSender";
import { InvoicePartyAddressFields } from "./InvoicePartyAddressFields";
import { NativeInvoiceFromState } from "./nativeInvoiceFormTypes";
import { useNativeInvoiceFormContext } from "./NativeInvoiceFormContext";
import styles from "./SidePanel.module.scss";

interface Props {
    entities: Entity[];
    selectedEntity?: Entity;
    onSelectEntity: (entity: Entity | undefined) => void;
    // The From entity can't be changed once an invoice exists (it drives
    // accounting/JE posting and branding), or when catalog items on the
    // invoice are scoped to the selected entity.
    isFromEntityLocked?: boolean;
    fromEntityLockedTooltip?: string;
}

// The "From" (sender) block of the Details step: entity picker (with the
// sending address shown inline), plus contact fields (email, full name,
// address, phone). Company always comes from the selected entity. A field
// prints on the invoice whenever it has a value.
export const DetailsFromFields: React.FC<Props> = ({
    entities,
    selectedEntity,
    onSelectEntity,
    isFromEntityLocked,
    fromEntityLockedTooltip,
}) => {
    const { state, setState } = useNativeInvoiceFormContext();
    const [showAllFields, setShowAllFields] = useState(false);
    const { senderOptions, selectedSenderValue } =
        useNativeInvoiceSenderOptions(
            state.sendingEmailId,
            selectedEntity?.uuid,
        );

    const updateFrom = useCallback(
        (patch: Partial<NativeInvoiceFromState>) =>
            setState((prev) => ({ ...prev, from: { ...prev.from, ...patch } })),
        [setState],
    );

    return (
        <>
            <div
                className={classNames(
                    styles.groupSubtitleRow,
                    styles.groupSubtitleRowTight,
                )}
            >
                <div className={styles.groupSubtitleSecondary}>From</div>
                <div className={styles.subtitleActionsCluster}>
                    <AllFieldsToggle
                        isActive={showAllFields}
                        onChanged={setShowAllFields}
                    />
                </div>
            </div>
            <div className={classNames(styles.fieldGrid, styles.fieldGridWide)}>
                <FromEntitySelect
                    entities={entities}
                    selectedEntity={selectedEntity}
                    onSelectEntity={onSelectEntity}
                    senderOptions={senderOptions}
                    selectedSenderValue={selectedSenderValue}
                    onSelectSender={(senderValue) =>
                        setState((prev) => ({
                            ...prev,
                            sendingEmailId:
                                senderValue === KICK_SENDER_VALUE
                                    ? null
                                    : senderValue,
                        }))
                    }
                    isEntityLocked={isFromEntityLocked}
                    entityLockedTooltip={fromEntityLockedTooltip}
                />
            </div>
            {showAllFields && (
                <>
                    <Field label="Full name">
                        <Input
                            size="xl"
                            maxLength={255}
                            value={state.from.name}
                            onChange={(event) =>
                                updateFrom({ name: event.target.value })
                            }
                        />
                    </Field>
                    <Field label="Reply to">
                        <Input
                            size="xl"
                            type="email"
                            maxLength={255}
                            value={state.replyToEmail}
                            onChange={(event) =>
                                setState((prev) => ({
                                    ...prev,
                                    replyToEmail: event.target.value,
                                }))
                            }
                        />
                    </Field>
                    <InvoicePartyAddressFields
                        values={{
                            country: state.from.country,
                            street: state.from.street,
                            street2: state.from.street2,
                            city: state.from.city,
                            state: state.from.state,
                            postalCode: state.from.postalCode,
                            phoneNumber: state.from.phoneNumber,
                        }}
                        onChange={updateFrom}
                    />
                </>
            )}
        </>
    );
};
